import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import EvokePillars from '../components/EvokePillars';
import EditorialMethod from '../components/EditorialMethod';
import TextMarquee from '../components/TextMarquee';
import ImageMarquee from '../components/ImageMarquee';
import { usePageImages } from '../hooks/usePageImages';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Evoke = () => {
  const { language } = useLanguage();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const { getImage, getImages } = usePageImages('evoke');

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await fetch(`${API_URL}/api/services`);
      if (response.ok) {
        const data = await response.json();
        setServices(data);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  // Default services as fallback
  const defaultServices = {
    en: [
      {
        number: '01',
        title: 'Brand Identity',
        tagline: "A visual system that feels inevitable",
        description: "A full visual identity system — type, color, voice, visuals — that feel like they were destined to exist.",
        deliverables: ['Logo & Visual System', 'Brand Guidelines', 'Typography Suite', 'Color Palette', 'Voice & Tone Guide'],
        is_dark: false
      },
      {
        number: '02', 
        title: 'Launch Campaigns',
        tagline: "Turn products into cultural moments",
        description: "Editorial-driven campaigns that turn products into moments & stories people want to share.",
        deliverables: ['Campaign Strategy', 'Content Architecture', 'Launch Rollout Plan', 'Social Strategy', 'Press Kit'],
        is_dark: true
      },
      {
        number: '03',
        title: 'UGC Content',
        tagline: "Authentic storytelling that converts",
        description: "Scroll-stopping videos, authentic storytelling, and brand-safe edge that wins both trust and attention.",
        deliverables: ['Content Strategy', 'Creator Sourcing', 'Video Production', 'Performance Tracking', 'Asset Library'],
        is_dark: false
      }
    ],
    es: [
      {
        number: '01',
        title: 'Identidad de Marca',
        tagline: "Un sistema visual que se siente inevitable",
        description: "Un sistema de identidad visual completo — tipografía, color, voz, visuales — que se sienten como si estuvieran destinados a existir.",
        deliverables: ['Logo y Sistema Visual', 'Guía de Marca', 'Suite Tipográfica', 'Paleta de Colores', 'Guía de Voz y Tono'],
        is_dark: false
      },
      {
        number: '02',
        title: 'Campañas de Lanzamiento', 
        tagline: "Convierte productos en momentos culturales",
        description: "Campañas editoriales que convierten productos en momentos e historias que la gente quiere compartir.",
        deliverables: ['Estrategia de Campaña', 'Arquitectura de Contenido', 'Plan de Lanzamiento', 'Estrategia Social', 'Kit de Prensa'],
        is_dark: true
      },
      {
        number: '03',
        title: 'Contenido UGC',
        tagline: "Storytelling auténtico que convierte",
        description: "Videos que detienen el scroll, storytelling auténtico y un edge seguro para la marca que gana confianza y atención.",
        deliverables: ['Estrategia de Contenido', 'Búsqueda de Creadores', 'Producción de Video', 'Seguimiento de Rendimiento', 'Biblioteca de Activos'],
        is_dark: false
      }
    ]
  };

  // Use API services if available, otherwise use defaults
  const displayServices = services.length > 0 ? services : defaultServices[language];

  const content = {
    en: {
      heroOverline: 'Our Services',
      heroHeadline: 'Permission To Stop Playing Nice',
      heroSubheadline: '(...at least with your marketing)',
      heroDescription: "We're not in the business of half-measures or check-the-box marketing. Our work is designed to spark obsession, stir culture, and make competitors wonder how you pulled it off.",
      heroCta: 'Get Started',
      whatsIncluded: "What's included:",

      manifestoTitle: 'Words I Live By',
      manifestoLines: [
        'Intention over noise.',
        'Taste is a strategy.',
        'Luxury is consistency.',
        'Details are the difference.',
        'Execution is the brand.',
      ],

      aboutTitle: 'About the Founder',
      aboutLines: [
        'Founder / Creative Director.',
        'Built in hospitality, where execution is the standard.',
        'I design moments that travel across the room and across digital.'
      ],

      partnersTitle: 'Ways to Partner',
      partners: [
        { title: 'Brand Partnerships', path: '/contact' },
        { title: 'Ciano Gourmet (DR)', path: '/ventures/ciano-gourmet' },
        { title: 'Ciano Bites (US)', path: '/ventures/ciano-bites' },
        { title: 'StaffDO', path: '/ventures/staffdo' },
        { title: 'Kora OS', path: '/ventures/kora-os' },
      ],

      ctaHeadline: "If your brand is building a moment — build it on purpose.",
      ctaSubheadline: "Response within 24-48 hours",
      ctaButton: "Partner With Me",

      marqueeText: ["it's about time you", "Took", "Up", "Space."],
      readFullStory: "Read the full story"
    },
    es: {
      heroOverline: 'Nuestros Servicios',
      heroHeadline: 'Permiso Para Dejar De Jugar Bonito',
      heroSubheadline: '(...al menos con tu marketing)',
      heroDescription: "No estamos en el negocio de las medias tintas o el marketing de marcar casillas. Nuestro trabajo está diseñado para provocar obsesión, agitar la cultura y hacer que los competidores se pregunten cómo lo lograste.",
      heroCta: 'Comenzar',
      whatsIncluded: 'Qué incluye:',

      manifestoTitle: 'Palabras Por Las Que Vivo',
      manifestoLines: [
        'Intención sobre ruido.',
        'El buen gusto es una estrategia.',
        'El lujo es consistencia.',
        'Los detalles hacen la diferencia.',
        'La ejecución es la marca.',
      ],

      aboutTitle: 'Sobre la Fundadora',
      aboutLines: [
        'Fundadora / Directora Creativa.',
        'Formada en hospitalidad, donde la ejecución es el estándar.',
        'Diseño momentos que viajan por el espacio y por lo digital.'
      ],

      partnersTitle: 'Formas de Colaborar',
      partners: [
        { title: 'Alianzas de Marca', path: '/contact' },
        { title: 'Ciano Gourmet (RD)', path: '/ventures/ciano-gourmet' },
        { title: 'Ciano Bites (US)', path: '/ventures/ciano-bites' },
        { title: 'StaffDO', path: '/ventures/staffdo' },
        { title: 'Kora OS', path: '/ventures/kora-os' },
      ],

      ctaHeadline: "Si tu marca está construyendo un momento — constrúyelo con propósito.",
      ctaSubheadline: "Respuesta en 24-48 horas",
      ctaButton: "Colaboremos",

      marqueeText: ["es hora de que", "Tomes", "Tu", "Espacio."],
      readFullStory: "Lee la historia completa"
    }
  };

  const t = content[language];

  // Helper function to get localized service data
  const getServiceField = (service, field) => {
    if (language === 'es') {
      return service[`${field}_es`] || service[field];
    }
    return service[field];
  };

  return (
    <div className="bg-vainilla" data-testid="evoke-page">
      {/* Hero Section - Bold Editorial */}
      <section className="min-h-screen flex items-center bg-stone-900 text-white relative overflow-hidden">
        {/* Background Video Placeholder */}
        <div className="absolute inset-0 bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900 opacity-90"></div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-6 py-32 text-center">
          <p className="text-terracota text-sm md:text-base font-bold tracking-[0.3em] uppercase mb-8">
            {t.heroOverline}
          </p>
          
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl font-bold leading-[0.95] mb-4">
            {t.heroHeadline}
          </h1>
          
          <p className="font-serif text-xl md:text-2xl italic text-stone-400 mb-8">
            {t.heroSubheadline}
          </p>
          
          <p className="text-lg md:text-xl text-stone-400 max-w-3xl mx-auto mb-12 leading-relaxed">
            {t.heroDescription}
          </p>
          
          <Link to="/contact">
            <button 
              className="inline-flex items-center gap-3 bg-terracota text-white px-10 py-5 text-base font-semibold uppercase tracking-wider hover:bg-white hover:text-stone-900 transition-all duration-300 rounded-full"
              data-testid="evoke-hero-cta"
            >
              {t.heroCta}
              <ArrowRight className="w-5 h-5" />
            </button>
          </Link>
        </div>
      </section>

      {/* Scrolling Text Marquee */}
      <TextMarquee 
        lines={t.marqueeText}
        speed={30}
        dark={false}
      />

      {/* Services Sections - Dynamic from API */}
      {displayServices.map((service, index) => {
        const isDark = service.is_dark ?? (index % 2 === 1);
        const serviceTitle = getServiceField(service, 'title');
        const serviceTagline = getServiceField(service, 'tagline');
        const serviceDescription = getServiceField(service, 'description');
        const serviceDeliverables = getServiceField(service, 'deliverables') || [];
        const serviceCta = getServiceField(service, 'cta_text') || t.heroCta;
        const serviceCtaLink = service.cta_link || '/contact';

        return (
          <section 
            key={service.id || service.number}
            className={`py-20 md:py-32 ${isDark ? 'bg-stone-900 text-white' : 'bg-vainilla text-stone-900'}`}
            data-testid={`service-${service.number}`}
          >
            <div className="max-w-7xl mx-auto px-6">
              <div className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center`}>
                
                {/* Content */}
                <div className={isDark ? 'lg:order-2' : 'lg:order-1'}>
                  <span className={`inline-block text-sm font-bold tracking-[0.2em] uppercase mb-6 px-4 py-2 rounded-full ${
                    isDark ? 'bg-stone-800 text-terracota' : 'bg-champagne text-terracota'
                  }`}>
                    {service.number}
                  </span>

                  <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
                    {serviceTitle}
                  </h2>

                  {serviceTagline && (
                    <p className={`text-xl md:text-2xl italic mb-6 ${isDark ? 'text-stone-400' : 'text-stone-500'}`}>
                      {serviceTagline}
                    </p>
                  )}

                  <p className={`text-lg leading-relaxed mb-8 ${isDark ? 'text-stone-400' : 'text-stone-600'}`}>
                    {serviceDescription}
                  </p>

                  {serviceDeliverables.length > 0 && (
                    <div className="mb-10">
                      <p className={`text-xs font-bold tracking-[0.2em] uppercase mb-4 ${isDark ? 'text-stone-500' : 'text-stone-400'}`}>
                        {t.whatsIncluded}
                      </p>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {serviceDeliverables.map((item, idx) => (
                          <li 
                            key={idx}
                            className={`flex items-center gap-3 text-sm ${isDark ? 'text-stone-300' : 'text-stone-700'}`}
                          >
                            <span className="w-1.5 h-1.5 bg-terracota rounded-full flex-shrink-0"></span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <Link to={serviceCtaLink}>
                    <button className={`inline-flex items-center gap-3 px-8 py-4 text-sm font-semibold uppercase tracking-wider transition-all duration-300 rounded-full ${
                      isDark
                        ? 'bg-white text-stone-900 hover:bg-terracota hover:text-white' 
                        : 'bg-stone-900 text-white hover:bg-terracota'
                    }`}>
                      {serviceCta}
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </Link>
                </div>

                {/* Image Placeholder */}
                <div className={isDark ? 'lg:order-1' : 'lg:order-2'}>
                  <div className={`aspect-[4/5] rounded-2xl overflow-hidden ${isDark ? 'bg-stone-800' : 'bg-champagne'}`}>
                    {(service.image_url || getImage(`service-${service.number.replace(/\D/g, '')}`)) ? (
                      <img 
                        src={service.image_url || getImage(`service-${service.number.replace(/\D/g, '')}`)} 
                        alt={serviceTitle}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className={`font-serif text-[180px] md:text-[220px] ${isDark ? 'text-stone-700' : 'text-terracota/10'}`}>
                          {service.number.replace(/\D/g, '')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* Image Marquee */}
      <section className="py-16 bg-vainilla overflow-hidden">
        <ImageMarquee images={getImages('gallery')} speed={40} grayscale={true} />
      </section>

      {/* The Evoke Pillars - User's Custom Design */}
      <EvokePillars />

      {/* Method Section */}
      <EditorialMethod />

      {/* Manifesto / Words I Live By */}
      <section className="py-24 md:py-32 bg-vainilla">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-stone-900 mb-12">
            {t.manifestoTitle}
          </h2>
          <div className="space-y-4">
            {t.manifestoLines.map((line, idx) => (
              <p 
                key={idx}
                className="font-serif text-2xl md:text-3xl lg:text-4xl text-stone-700 italic leading-snug"
              >
                {line}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* About Block */}
      <section className="py-20 md:py-28 bg-champagne">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-stone-900 mb-8">
            {t.aboutTitle}
          </h2>
          <div className="space-y-4 mb-10">
            {t.aboutLines.map((line, idx) => (
              <p key={idx} className="text-lg md:text-xl text-stone-600 leading-relaxed">
                {line}
              </p>
            ))}
          </div>
          <Link to="/about" className="inline-flex items-center gap-2 text-terracota font-semibold hover:gap-3 transition-all">
            {t.readFullStory}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Ways to Partner */}
      <section className="py-20 md:py-28 bg-vainilla">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-serif text-3xl md:text-4xl text-stone-900 mb-12 text-center">
            {t.partnersTitle}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {t.partners.map((partner, idx) => (
              <Link 
                key={idx}
                to={partner.path}
                className="group p-6 bg-white border border-stone-200 rounded-xl hover:border-terracota hover:shadow-lg transition-all"
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-stone-900 group-hover:text-terracota transition-colors">
                    {partner.title}
                  </span>
                  <ArrowRight className="w-5 h-5 text-stone-400 group-hover:text-terracota group-hover:translate-x-1 transition-all" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32 bg-terracota text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
            {t.ctaHeadline}
          </h2>
          <p className="text-xl md:text-2xl text-white/80 mb-12">
            {t.ctaSubheadline}
          </p>
          <Link to="/contact">
            <button 
              className="inline-flex items-center gap-3 bg-white text-terracota px-10 py-5 text-lg font-semibold uppercase tracking-wider hover:bg-stone-900 hover:text-white transition-all duration-300 rounded-full"
              data-testid="evoke-final-cta"
            >
              {t.ctaButton}
              <ArrowRight className="w-5 h-5" />
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Evoke;
